/**
 * BoFH API - Booking response
 */
export interface Booking {
  /**
   * Anonymous ID
   */
  anonymousId: string
  /**
   * Billing information
   */
  billing: Billing
  /**
   * Denotes whether the booking has multiple provider bookings (bundle) or a single provider
   * booking (regular).
   */
  bookingType: BookingType
  /**
   * Brand code
   */
  brandCode?: null | string
  /**
   * Booking ID of the other booking that composes the Split Bookings bundle.
   */
  bundleBookingId?: null | string
  /**
   * Cancellation ID
   */
  cancellationId?: null | string
  /**
   * List of cancellation penalties
   */
  cancellationPenalties: CancellationPenalty[]
  /**
   * Cancellation policy
   */
  cancellationPolicy: CancellationPolicy
  /**
   * The date and time (UTC) on which the booking was cancelled
   */
  cancelledAt?: null | string
  /**
   * Cashback information
   */
  cashback?: null | Cashback
  /**
   * The date and time (UTC) on which the booking was charged
   */
  chargedAt?: null | string
  /**
   * The first charge attempt due date.
   */
  chargeDueAt?: null | string
  /**
   * Check-in date
   */
  checkIn: string
  /**
   * Check-out date
   */
  checkOut: string
  /**
   * The same value from providerConfirmationId property.
   */
  confirmationId: string
  /**
   * Currency conversion rate.
   */
  currencyConversionRate?: null | CurrencyConversionRate
  /**
   * Customer information
   */
  customer: Customer
  /**
   * Customer ISO-3166-1 alpha-2 country code
   */
  customerCountryCode?: null | string
  /**
   * Customer IP
   */
  customerIp: string
  /**
   * Customer support information
   */
  customerSupport: CustomerSupport
  /**
   * The date and time (UTC) on which an unpaid booking expires
   */
  expiresAt?: null | string
  /**
   * The date on which the booking fulfills
   */
  fulfillmentDate?: null | string
  /**
   * Hotel ID
   */
  hotelId: string
  /**
   * Booking ID
   */
  id: string
  /**
   * The date and time (UTC) on which the booking was created
   */
  insertedAt: string
  /**
   * Instructions
   */
  instructions: Instructions
  /**
   * Links generated for the booking
   */
  links: Link[]
  /**
   * Name of the entity that processes the customer payment.
   */
  merchantOfRecord: MerchantOfRecord | null
  /**
   * The booking offer identifier
   */
  offerId?: null | string
  /**
   * Offer metadata
   */
  offerMetadata: OfferMetadata
  /**
   * Payment information.
   */
  payment?: null | Payment
  /**
   * The payment mode consolidated when the booking was created. `pay_at_property` means the
   * property collects the payment, `charge_later` means the customer chose to be charged
   * later, `pay_now` means the customer paid at booking. On an offer check the same field
   * means eligibility instead, so an offer eligible for charge later that was booked through
   * the regular endpoint is recorded here as `pay_now`.
   */
  paymentMode: PaymentMode
  /**
   * History of price changes for the booking.
   */
  priceChanges: PriceChange[]
  /**
   * Prices
   */
  prices: Price[]
  /**
   * Confirmation ID that a provider needs in their customer support
   */
  providerConfirmationId: string
  /**
   * Provider trader information
   */
  providerTraderInformation?: ProviderTraderInformation
  /**
   * The redirect ID of the booking.
   */
  redirectId?: string
  /**
   * Refund informations
   */
  refund?: null | Refund
  /**
   * Rooms
   */
  rooms: Room[]
  /**
   * Booking source
   */
  source: Source
  /**
   * Booking status
   */
  status: BookingStatus
  /**
   * The date and time (UTC) on which the booking was last time updated
   */
  updatedAt: string
}

/**
 * Billing information
 */
export interface Billing {
  /**
   * Address
   */
  address: null | Address
  /**
   * First name
   */
  firstName: string
  /**
   * Last name
   */
  lastName: string
}

export interface Address {
  /**
   * City
   */
  city: string
  /**
   * Country code
   */
  countryCode: string
  /**
   * Address line one
   */
  line1: string
  /**
   * Address line two
   */
  line2?: null | string
  /**
   * Postal code
   */
  postalCode: string
  /**
   * State code
   */
  stateCode?: null | string
}

/**
 * Denotes whether the booking has multiple provider bookings (bundle) or a single provider
 * booking (regular).
 */
export enum BookingType {
  Bundle = 'bundle',
  Regular = 'regular'
}

/**
 * Cancellation penalty
 */
export interface CancellationPenalty {
  /**
   * Fixed amount of the penalty
   */
  amount: string
  /**
   * Currency of the fixed amount
   */
  currency: string
  /**
   * End date and time of the penalty
   */
  end: string
  /**
   * Start date and time of the penalty
   */
  start: string
}

/**
 * Cancellation policy
 */
export interface CancellationPolicy {
  /**
   * Deadline for free cancellation
   */
  deadline?: null | string
  /**
   * Description
   */
  description: string
}

export interface Cashback {
  /**
   * Cashback type
   */
  type: CashbackType
  /**
   * Cashback value
   */
  value: number
}

/**
 * Cashback type
 */
export enum CashbackType {
  Points = 'points'
}

export interface CurrencyConversionRate {
  /**
   * The source currency of the conversion
   */
  fromCurrency: string
  /**
   * The inverse exchange rate (1 / rate) from toCurrency to fromCurrency
   */
  inverseRate: string
  /**
   * The exchange rate from fromCurrency to toCurrency
   */
  rate: string
  /**
   * The target currency of the conversion
   */
  toCurrency: string
}

/**
 * Customer information
 */
export interface Customer {
  /**
   * Email
   */
  email: string
  /**
   * First name
   */
  firstName: string
  /**
   * Last name
   */
  lastName: string
  /**
   * Phone number
   */
  phoneNumber?: null | string
}

/**
 * Customer support information
 */
export interface CustomerSupport {
  /**
   * Email
   */
  email: string
  /**
   * Phone number
   */
  phoneNumber?: null | string
}

/**
 * Instructions
 */
export interface Instructions {
  /**
   * The time at which a hotel stops allowing check-ins
   */
  checkInEnd?: null | string
  /**
   * The minimum age for a customer to be able to check-in at a property
   */
  checkInMinimumAge?: number | null
  /**
   * The time at which a hotel begins to allow check-ins
   */
  checkInStart?: null | string
  /**
   * The time by which a guest must check out
   */
  checkOutEnd?: null | string
  /**
   * Additional instructions from the hotel
   */
  details?: null | string
}

/**
 * Link
 */
export interface Link {
  /**
   * URL
   */
  href: string
  /**
   * HTTP method
   */
  method: Method
  /**
   * Type
   */
  type: LinkType
}

/**
 * HTTP method
 */
export enum Method {
  Delete = 'DELETE',
  Get = 'GET',
  Post = 'POST',
  Put = 'PUT'
}

/**
 * Type
 */
export enum LinkType {
  BookingCancellation = 'booking_cancellation',
  BookingConfirmationNotification = 'booking_confirmation_notification',
  BookingPayment = 'booking_payment',
  BookingStatus = 'booking_status',
  Invoice = 'invoice',
  InvoiceCreation = 'invoice_creation',
  ManageBooking = 'manage_booking',
  PaymentMethodsSettings = 'payment_methods_settings'
}

export enum MerchantOfRecord {
  Expedia = 'expedia',
  Findhotel = 'findhotel',
  Getaroom = 'getaroom',
  Hotel = 'hotel',
  Priceline = 'priceline'
}

/**
 * Offer metadata
 */
export interface OfferMetadata {
  /**
   * Information to help keep membership experience across the whole booking funnel
   */
  accessTier: string[]
}

export interface Payment {
  /**
   * Whether the payment authentication should occur later.
   */
  authenticateLater?: boolean
  /**
   * Booking charge attempts
   */
  chargeAttempts?: ChargeAttempt[]
  /**
   * if it is payment for high value booking
   */
  highValue?: boolean | null
  /**
   * Payment method
   */
  method: PaymentMethod
  /**
   * Saved payment method information.
   */
  savedPaymentMethod?: SavedPaymentMethod
  /**
   * Payment status.
   */
  status?: PaymentStatus
}

/**
 * Charge Attempt
 */
export interface ChargeAttempt {
  /**
   * Attempted At
   */
  attemptedAt: string
}

/**
 * Payment method
 */
export interface PaymentMethod {
  /**
   * Card information.
   */
  card?: null | Card
  /**
   * The payment type.
   */
  type: MethodType
}

export interface Card {
  /**
   * The card brand.
   */
  brand: Brand
  /**
   * The last four digits of the card.
   */
  lastFourDigits: string
}

/**
 * The card brand.
 */
export enum Brand {
  AmericanExpress = 'american_express',
  DinersClub = 'diners_club',
  Discover = 'discover',
  Jcb = 'jcb',
  Maestro = 'maestro',
  MasterCard = 'master_card',
  MasterCardDebit = 'master_card_debit',
  UnionPay = 'union_pay',
  Visa = 'visa',
  VisaDebit = 'visa_debit',
  VisaElectron = 'visa_electron'
}

/**
 * The payment type.
 */
export enum MethodType {
  Card = 'card',
  Ideal = 'ideal',
  Klarna = 'klarna',
  RevolutPay = 'revolut_pay',
  Vipps = 'vipps'
}

/**
 * Saved payment method information.
 */
export interface SavedPaymentMethod {
  /**
   * Whether the booking is eligible to use the saved payment method.
   */
  eligible?: boolean
}

/**
 * Payment status.
 */
export enum PaymentStatus {
  Failed = 'failed',
  Pending = 'pending',
  Refunded = 'refunded',
  Succeeded = 'succeeded',
  ThreeDomainSecureRequired = 'three_domain_secure_required'
}

/**
 * The payment mode consolidated when the booking was created. `pay_at_property` means the
 * property collects the payment, `charge_later` means the customer chose to be charged
 * later, `pay_now` means the customer paid at booking. On an offer check the same field
 * means eligibility instead, so an offer eligible for charge later that was booked through
 * the regular endpoint is recorded here as `pay_now`.
 */
export enum PaymentMode {
  ChargeLater = 'charge_later',
  PayAtProperty = 'pay_at_property',
  PayNow = 'pay_now'
}

export interface PriceChange {
  /**
   * The date and time (UTC) when the price change occurred.
   */
  createdAt: string
  price: Price
}

/**
 * Price
 */
export interface Price {
  /**
   * Chargeable information
   */
  chargeable: Chargeable
  currencyCode: string
  /**
   * Hotel fees information
   */
  hotelFees: HotelFees
  /**
   * Taxes information, providing a detailed breakdown of applicable taxes.
   */
  taxes: null | Taxes
  /**
   * The type of the price
   */
  type: PriceType
}

/**
 * Chargeable information
 */
export interface Chargeable {
  /**
   * Total rate without taxes
   */
  base: string
  currency: string
  /**
   * Total of all taxes
   */
  taxes: string
  /**
   * Total of base rate and taxes
   */
  total: string
}

/**
 * Hotel fees information
 */
export interface HotelFees {
  /**
   * The breakdown of the hotel fees per type.
   */
  breakdown: HotelFeesBreakdownItem[]
  currency: string
  /**
   * Total of all hotel fees
   */
  total: string
}

export interface HotelFeesBreakdownItem {
  /**
   * Raw charge inputs (mode + value) for the breakdown row. Emitted today only on local_vat
   * rows and omitted entirely otherwise.
   */
  charge?: HotelFeesBreakdownCharge
  /**
   * The amount charged for the type.
   */
  total: string
  /**
   * The type of fee charged.
   */
  type: BreakdownType
}

/**
 * Raw charge inputs (mode + value) for the breakdown row. Emitted today only on local_vat
 * rows and omitted entirely otherwise.
 */
export interface HotelFeesBreakdownCharge {
  /**
   * RAA charge mode that drove the calculation.
   */
  mode: Mode
  /**
   * Raw input value. For mode=percentage, expressed in percentage points ("18.0" = 18%).
   */
  value: string
}

/**
 * RAA charge mode that drove the calculation.
 */
export enum Mode {
  PerGuest = 'per_guest',
  PerGuestNight = 'per_guest_night',
  PerNightRoom = 'per_night_room',
  PerRoom = 'per_room',
  Percentage = 'percentage'
}

/**
 * The type of fee charged.
 */
export enum BreakdownType {
  CityTax = 'city_tax',
  CleaningFee = 'cleaning_fee',
  DestinationFee = 'destination_fee',
  EnvironmentFee = 'environment_fee',
  GoodsAndServicesTax = 'goods_and_services_tax',
  GovernmentTax = 'government_tax',
  LocalVat = 'local_vat',
  MunicipalityFee = 'municipality_fee',
  Other = 'other',
  ResortFee = 'resort_fee',
  ServiceCharge = 'service_charge',
  Tax = 'tax',
  TourismFee = 'tourism_fee'
}

export interface Taxes {
  /**
   * The breakdown of the taxes per type.
   */
  breakdown: TaxesBreakdown[]
  currency: string
  /**
   * Total of all taxes included in this breakdown.
   */
  total: string
}

export interface TaxesBreakdown {
  /**
   * The amount charged for the tax type.
   */
  total: string
  /**
   * The type of tax charged.
   */
  type: TaxesType
}

/**
 * The type of tax charged.
 */
export enum TaxesType {
  Tax = 'tax',
  Vat = 'vat'
}

/**
 * The type of the price
 */
export enum PriceType {
  ChargeableCurrency = 'chargeable_currency',
  ChargeablePrice = 'chargeable_price',
  HotelCurrency = 'hotel_currency',
  UserCurrency = 'user_currency'
}

/**
 * Provider trader information
 */
export interface ProviderTraderInformation {
  /**
   * Country code.
   */
  countryCode?: null | string
  /**
   * Trader name.
   */
  name?: null | string
  /**
   * Phone number
   */
  phoneNumber?: null | string
  /**
   * The URL of the terms and conditions. It can point to a PDF or a static HTML page.
   */
  termsAndConditionsUrl?: null | string
}

export interface Refund {
  /**
   * The refund amount.
   */
  amount: string
  /**
   * The refund amount currency.
   */
  currency: string
  /**
   * The status of the refund. For cancelled bookings, it will be `refunded`. For confirmed
   * bookings it will be `to_be_refunded`.
   */
  status: RefundStatus
}

/**
 * The status of the refund. For cancelled bookings, it will be `refunded`. For confirmed
 * bookings it will be `to_be_refunded`.
 */
export enum RefundStatus {
  Refunded = 'refunded',
  ToBeRefunded = 'to_be_refunded'
}

/**
 * Room
 */
export interface Room {
  /**
   * Bed type name
   */
  bedTypeName?: null | string
  /**
   * Describe what meals or drink options are included
   */
  boardType?: BoardType | null
  /**
   * Room check-in date
   */
  checkIn?: string
  /**
   * Room check-out date
   */
  checkOut?: string
  /**
   * Confirmation ID
   */
  confirmationId: string
  /**
   * Guest first name
   */
  firstName: string
  /**
   * Guests
   */
  guests: Guests
  /**
   * Room ID
   */
  id: null | string
  /**
   * Guest last name
   */
  lastName: string
  /**
   * Room master ID
   */
  masterId: null | string
  /**
   * Room meal plan offered for the rate, with optional occupancy rules
   */
  mealPlan?: MealPlan[]
  /**
   * Room name
   */
  name: string
  /**
   * Provider room ID
   */
  providerRoomId?: null | string
  /**
   * Provider room name
   */
  providerRoomName?: null | string
  /**
   * Sanitized room name
   */
  sanitizedName?: null | string
  /**
   * Smoking
   */
  smoking?: boolean | null
  /**
   * Special request
   */
  specialRequest?: null | string
}

export enum BoardType {
  AllInclusive = 'all_inclusive',
  Breakfast = 'breakfast',
  Dinner = 'dinner',
  FullBoard = 'full_board',
  HalfBoard = 'half_board',
  Lunch = 'lunch',
  RoomOnly = 'room_only'
}

/**
 * Guests
 */
export interface Guests {
  /**
   * Quantity of adults
   */
  adults: number
  /**
   * Child ages
   */
  childAges: number[]
  /**
   * Quantity of children
   */
  children: number
}

/**
 * Meal plan entry
 */
export interface MealPlan {
  /**
   * Meal type included in the plan
   */
  meal: Meal
  /**
   * Occupancy the meal applies to
   */
  rule?: MealPlanRule
}

/**
 * Meal type included in the plan
 */
export enum Meal {
  AllInclusive = 'allInclusive',
  Breakfast = 'breakfast',
  Dinner = 'dinner',
  FullBoard = 'fullBoard',
  HalfBoard = 'halfBoard',
  Lunch = 'lunch'
}

/**
 * Occupancy the meal applies to
 */
export interface MealPlanRule {
  /**
   * Number of adults the meal applies to
   */
  adults?: number
  /**
   * Number of guests the meal applies to
   */
  guests?: number
  /**
   * Number of kids the meal applies to
   */
  kids?: number
}

/**
 * Booking source
 */
export enum Source {
  ChargeLater = 'charge_later',
  DealFreeze = 'deal_freeze',
  Default = 'default'
}

/**
 * Booking status
 */
export enum BookingStatus {
  Cancelled = 'cancelled',
  ChargeLaterCancelled = 'charge_later_cancelled',
  ChargeLaterConfirmed = 'charge_later_confirmed',
  ChargeLaterExpired = 'charge_later_expired',
  ChargeLaterFailed = 'charge_later_failed',
  Confirmed = 'confirmed',
  Error = 'error',
  FreezeCancelled = 'freeze_cancelled',
  FreezeExpired = 'freeze_expired',
  Frozen = 'frozen',
  Initialized = 'initialized',
  // persist manually until sc-192878 ships.
  PaymentAuthenticateLaterCancelled = 'payment_authenticate_later_cancelled',
  PaymentAuthenticateLaterConfirmed = 'payment_authenticate_later_confirmed',
  PaymentAuthenticateLaterExpired = 'payment_authenticate_later_expired',
  Rebooked = 'rebooked',
  ThreeDomainSecureRequired = 'three_domain_secure_required'
}
